import React, {CSSProperties, FC} from "react";
import {Card, Col, Divider, Row, Tag, Typography} from "antd";
import {
    AimOutlined,
    CrownOutlined,
    InfoCircleOutlined,
    StarOutlined,
    ThunderboltOutlined,
    TrophyOutlined,
} from "@ant-design/icons";

const {Title, Paragraph, Text} = Typography;

const ACCENT = '#203923';
const ACCENT_SOFT = '#E8F0E9';

const scoreCategories = [
    {pts: 1,  label: 'Incorrect',  color: '#F97316', example: 'Prono 2-1, réel 1-2', desc: "Le résultat est faux mais on gratifie tout de même la participation."},
    {pts: 5,  label: 'Tendance',   color: '#3B82F6', example: 'Prono 2-1, réel 3-1', desc: "Le bon vainqueur (ou le match nul) a été identifié."},
    {pts: 9,  label: 'Différence', color: '#14B8A6', example: 'Prono 2-1, réel 4-3', desc: "L'écart de buts entre les deux équipes est exact."},
    {pts: 20, label: 'Exact',      color: '#10B981', example: 'Prono 2-1, réel 2-1', desc: "Le score précis a été deviné — récompense maximale."},
];

const bonusQuestions = [
    {q: 'Qui gagnera le Championnat ?',                                   pts: 50},
    {q: 'Qui sera Finaliste vaincu ?',                                    pts: 37},
    {q: 'Qui sortira vainqueur de chacun des groupes (A à H) ?',          pts: 22},
    {q: 'Meilleure attaque (plus grand nombre de buts) ?',                pts: 11},
    {q: 'Meilleure défense (plus petit nombre de buts encaissés) ?',      pts: 11},
];

const sectionTitle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    color: ACCENT,
    marginTop: 0,
    marginBottom: 4,
};

const sectionCard: CSSProperties = {
    marginBottom: 20,
    borderRadius: 12,
    border: '1px solid #E5E7EB',
};

const calloutBox: CSSProperties = {
    background: ACCENT_SOFT,
    borderLeft: `4px solid ${ACCENT}`,
    padding: '12px 16px',
    borderRadius: 6,
    margin: '12px 0',
};

const About: FC = () => {
    return (
        <div style={{
            maxWidth: 980,
            margin: '0 auto',
            padding: '24px 24px 48px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            color: '#1F2937',
            textAlign: 'left',
            lineHeight: 1.6,
        }}>

            <div style={{textAlign: 'center', marginBottom: 32}}>
                <Title level={1} style={{color: ACCENT, marginBottom: 8}}>
                    Fonctionnement de Sports-Bets
                </Title>
                <Paragraph type="secondary" style={{fontSize: 16, marginBottom: 0}}>
                    Tout ce qu'il faut savoir pour pronostiquer et marquer un maximum de points.
                </Paragraph>
            </div>

            <Card style={sectionCard} bodyStyle={{padding: 24}}>
                <Title level={3} style={sectionTitle}>
                    <TrophyOutlined/> Les deux groupes de paris
                </Title>
                <Paragraph type="secondary" style={{marginBottom: 20}}>
                    Sports-Bets repose sur deux types de paris complémentaires.
                </Paragraph>

                <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                        <Card type="inner" title={<><StarOutlined/> Résultats finaux</>} style={{height: '100%'}}>
                            <Paragraph type="secondary" style={{marginBottom: 12}}>
                                À effectuer <Text strong>avant le début de la compétition</Text> (cf. Questions Bonus).
                            </Paragraph>
                            <ul style={{paddingLeft: 20, margin: 0}}>
                                <li>Qui gagnera la compétition ?</li>
                                <li>Qui sera finaliste vaincu ?</li>
                                <li>Qui sortira vainqueur de chacun des groupes (A à H) ?</li>
                                <li>Meilleure attaque ?</li>
                                <li>Meilleure défense ?</li>
                            </ul>
                            <Paragraph type="secondary" style={{fontSize: 12, fontStyle: 'italic', marginTop: 12, marginBottom: 0}}>
                                Les paris attaque/défense sont calculés sur les 4 demi-finalistes.
                            </Paragraph>
                        </Card>
                    </Col>
                    <Col xs={24} md={12}>
                        <Card type="inner" title={<><AimOutlined/> Paris en cours de compétition</>} style={{height: '100%'}}>
                            <Paragraph style={{marginBottom: 0}}>
                                Pariez sur le résultat attendu de chaque match jusqu'à <Text strong>15 minutes avant le coup d'envoi</Text>
                                {' '}(ou jusqu'au début du match selon la configuration).
                            </Paragraph>
                        </Card>
                    </Col>
                </Row>
            </Card>

            <Card style={sectionCard} bodyStyle={{padding: 24}}>
                <Title level={3} style={sectionTitle}>
                    <ThunderboltOutlined/> Décompte des points
                </Title>
                <Paragraph type="secondary" style={{marginBottom: 20}}>
                    Quatre catégories selon la précision du pronostic. Plus c'est juste, plus ça rapporte.
                </Paragraph>

                <Row gutter={[12, 12]}>
                    {scoreCategories.map(c => (
                        <Col xs={24} sm={12} lg={6} key={c.pts}>
                            <div style={{
                                background: c.color,
                                color: '#fff',
                                borderRadius: 10,
                                padding: 16,
                                height: '100%',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            }}>
                                <div style={{fontSize: 36, fontWeight: 800, lineHeight: 1}}>{c.pts}</div>
                                <div style={{fontSize: 11, opacity: 0.85, letterSpacing: '0.05em', textTransform: 'uppercase', marginTop: 2}}>
                                    points
                                </div>
                                <div style={{fontSize: 16, fontWeight: 700, marginTop: 10}}>{c.label}</div>
                                <div style={{fontSize: 12, opacity: 0.95, marginTop: 4, fontStyle: 'italic'}}>{c.example}</div>
                                <div style={{fontSize: 12, opacity: 0.9, marginTop: 8, lineHeight: 1.4}}>{c.desc}</div>
                            </div>
                        </Col>
                    ))}
                </Row>

                <div style={calloutBox}>
                    <Text strong>Pourquoi 1 point pour un pari incorrect ?</Text>
                    <Paragraph style={{marginBottom: 0, marginTop: 4}}>
                        Pour donner un léger avantage à celui qui parie, par rapport à celui qui aurait oublié de poster son pari (0 point).
                    </Paragraph>
                </div>
            </Card>

            <Card style={sectionCard} bodyStyle={{padding: 24}}>
                <Title level={3} style={sectionTitle}>
                    <StarOutlined/> Bonus Score
                </Title>
                <Paragraph type="secondary" style={{marginBottom: 16}}>
                    Cumulables avec n'importe quelle catégorie ci-dessus, jusqu'à <Text strong>+4 points</Text> par match.
                </Paragraph>

                <Row gutter={[12, 12]}>
                    <Col xs={24} sm={12}>
                        <div style={{padding: 14, border: '1px dashed #D1D5DB', borderRadius: 8, background: '#FAFAFA'}}>
                            <Tag color="green" style={{marginBottom: 6}}>+2 pts</Tag>
                            <div>Nombre de buts de l'équipe <Text strong>domicile</Text> correct.</div>
                        </div>
                    </Col>
                    <Col xs={24} sm={12}>
                        <div style={{padding: 14, border: '1px dashed #D1D5DB', borderRadius: 8, background: '#FAFAFA'}}>
                            <Tag color="green" style={{marginBottom: 6}}>+2 pts</Tag>
                            <div>Nombre de buts de l'équipe <Text strong>extérieure</Text> correct.</div>
                        </div>
                    </Col>
                </Row>

                <Paragraph style={{marginTop: 16, marginBottom: 8}}>
                    Même avec un résultat globalement incorrect, on peut grappiller des points. Exemple : résultat <Text code>2-1</Text>, pari <Text code>0-1</Text> → <Text strong>2 points</Text> pour le score "extérieur" correct.
                </Paragraph>

                <div style={{...calloutBox, background: '#FEF3C7', borderLeftColor: '#F59E0B'}}>
                    <Text strong>Un pari "Exact" rapporte donc 24 points !</Text> (20 + 2 + 2)
                </div>
            </Card>

            <Card style={sectionCard} bodyStyle={{padding: 24}}>
                <Title level={3} style={sectionTitle}>
                    <InfoCircleOutlined/> Cas particuliers
                </Title>

                <div style={{marginTop: 12}}>
                    <Text strong>Match nul bien pronostiqué avec score différent</Text>
                    <Paragraph style={{marginBottom: 0, marginTop: 4}}>
                        Exemple : pari <Text code>1-1</Text>, résultat <Text code>2-2</Text>. La différence de buts (0) est correcte → catégorie <Tag color="cyan" style={{marginLeft: 4}}>Différence · 9 pts</Tag>
                        <Text type="secondary" style={{fontSize: 12}}> (paramétrable : peut être réglé sur 5 pts "Tendance" selon la configuration).</Text>
                    </Paragraph>
                </div>

                <Divider style={{margin: '16px 0'}}/>

                <div>
                    <Text strong>Phases à élimination directe</Text>
                    <Paragraph style={{marginBottom: 0, marginTop: 4}}>
                        On garde le résultat <Text strong>à la fin des prolongations</Text> (les tirs au but ne comptent pas). Un match nul reste donc possible.
                    </Paragraph>
                </div>
            </Card>

            <Card style={sectionCard} bodyStyle={{padding: 24}}>
                <Title level={3} style={sectionTitle}>
                    <CrownOutlined/> Questions Bonus
                </Title>
                <Paragraph type="secondary" style={{marginBottom: 20}}>
                    Attribuées en fin de championnat, ces réponses peuvent <Text strong>bouleverser le classement</Text> jusque-là établi.
                </Paragraph>

                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                    {bonusQuestions.map(b => (
                        <div key={b.q} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px 16px',
                            background: '#F9FAFB',
                            border: '1px solid #E5E7EB',
                            borderRadius: 8,
                        }}>
                            <span>{b.q}</span>
                            <Tag color={ACCENT} style={{margin: 0, fontSize: 13, padding: '2px 10px'}}>{b.pts} pts</Tag>
                        </div>
                    ))}
                </div>
            </Card>

            <Paragraph type="secondary" style={{textAlign: 'center', fontStyle: 'italic', fontSize: 12, marginTop: 24}}>
                Le nombre de points et le traitement du cas "match nul" sont paramétrables au lancement de chaque championnat.
            </Paragraph>

        </div>
    );
};

export default About;
